import React, { useEffect, useState } from 'react';
import styles from '../../_css/Pricing.module.css';
import {
  PageHead,
  SectionTitle,
  Panel,
  ActionsBar,
  Select,
  Btn,
} from './shared';
import { getPricingCreditLedger, getPricingSystemConfiguration, postPricingSystemConfiguration, getPricingChangeHistory, getAgencyList, getPricingConfigChangeLogs, downloadPricingConfigChangeLogs, downloadPricingCreditLedger } from '../../requests';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { formatDate } from '../helper/formatters';
import { notifyError } from '../../utils/helper';
import _ from 'lodash';

// Ledger rows whose "credits" value is a snapshot of the plan's included allotment,
// not an actual usage delta - these are the ones that carry the raw sentinel number for
// an Unlimited contract and need to read "Unlimited" instead.
const CREDIT_ALLOTMENT_ACTIONS = new Set(['contract creation', 'max credits change']);

export default function AuditSection() {
  const [ledgerRows, setLedgerRows] = useState([]);
  const [retentionYears, setRetentionYears] = useState('7');
  const [loadingLedger, setLoadingLedger] = useState(false);
  const [changeLog, setChangeLog] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [tenantFilter, setTenantFilter] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(5);
  const [totalPages, setTotalPages] = useState(1);
  const [configTotalPages, setConfigTotalPages] = useState(1);
  const [configPage, setConfigPage] = useState(1);
  const [configLimit, setConfigLimit] = useState(5);
  const [configLoading, setConfigLoading] = useState(false);
  const [exportingLog, setExportingLog] = useState(false);
  const [exportingLedger, setExportingLedger] = useState(false);

  const display = (v) => {
    if (v == null) return '';
    if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') return String(v);
    if (typeof v === 'object') return v.name || v.label || (v.id ? String(v.id) : JSON.stringify(v));
    return String(v);
  };

  useEffect(() => {
    const load = async () => {
      setLoadingLedger(true);
      try {
        const params = { page, limit };
        if (fromDate) params.from_date = fromDate;
        if (toDate) params.to_date = toDate;
        if (tenantFilter) {
          const id = Number(tenantFilter);
          params.tenant_id = id;
          params.agency_id = id; // also include agency_id for backend compatibility
        }
        if (actionFilter) params.action = actionFilter;

        const res = await getPricingCreditLedger(params);
        if (res && res.data) {
          const payload = res.data;
          const rows = Array.isArray(payload.data) ? payload.data : Array.isArray(payload) ? payload : [];
          setLedgerRows(rows.map((r) => ({
            timestamp: r.timestamp || r.created_at || r.when || r.date,
            agencyId: r.agency_id ?? r.agency?.id ?? r.tenant_id ?? r.tenant?.id ?? null,
            agency: r.agency?.name ?? r.agency ?? r.tenant?.name ?? r.tenant,
            actor: r?.edited_by_user?.full_name ?? r.user ?? r.by,
            action: r.action,
            type: r.type,
            credits: r.credits ?? r.amount ?? r.value,
            tier: r?.agency?.contracts?.[0]?.tier ?? r.tier,
            ref: r.ref ?? r.reference,
          })));

          // pagination
          const pag = payload.pagination || payload.meta || payload;
          if (pag) {
            const total = pag.total ?? pag.totalItems ?? pag.totalCount ?? pag.count ?? null;
            const perPage = pag.perPage ?? pag.per_page ?? pag.limit ?? limit;
            if (total != null && perPage) {
              setTotalPages(Math.max(1, Math.ceil(Number(total) / Number(perPage))));
            } else if (pag.totalPages) setTotalPages(pag.totalPages);
          }
        }

        const cfg = await getPricingSystemConfiguration({ key: 'audit_retention' });
        if (cfg && cfg.data && cfg.data.value) {
          setRetentionYears(String(cfg.data.value).replace(/[^0-9]/g, '') || '7');
        }
      } catch (e) {
        // ignore — keep mock data if API fails
      } finally {
        setLoadingLedger(false);
      }
    };
    load();
  }, [tenantFilter, actionFilter, fromDate, toDate, page, limit]);

  useEffect(() => {
    const loadConfigChangeLogs = async () => {
      setConfigLoading(true);
      const ch = await getPricingConfigChangeLogs({ page: configPage, limit: configLimit });
      const pg = ch?.data?.pagination || ch?.data?.meta || ch?.data;
      if (pg) {
        const total = pg.total ?? pg.totalItems ?? pg.totalCount ?? pg.count ?? null;
        const perPage = pg.perPage ?? pg.per_page ?? pg.limit ?? limit;
        if (total != null && perPage) {
          setConfigTotalPages(Math.max(1, Math.ceil(Number(total) / Number(perPage))));
        } else if (pg.totalPages) setConfigTotalPages(pg.totalPages);
      }
      const rows = Array.isArray(ch?.data?.data) ? ch.data.data : [];
      if (rows.length) setChangeLog(rows);
      setConfigLoading(false);
    };
    loadConfigChangeLogs();
  }, [configPage, configLimit]);

  // load tenants once
  useEffect(() => {
    const loadTenants = async () => {
      try {
        const aRes = await getAgencyList();
        const rows = Array.isArray(aRes?.data) ? aRes.data : Array.isArray(aRes?.data?.data) ? aRes.data.data : [];
        setTenants(rows.map((a) => ({ id: a?.id ?? a?.agency?.id, name: a?.name ?? a?.agency?.name })));
      } catch (err) {
        // ignore
      }
    };
    loadTenants();
  }, []);

  const saveRetention = async () => {
    try {
      await postPricingSystemConfiguration({ key: 'audit_retention', value: `${retentionYears} years` });
    } catch (e) {
      // ignore
    }
  };

  const onFilterChange = (setter) => (v) => {
    setter(v);
    setPage(1);
  };

  const getDownloadFilename = (response, fallback) => {
    const disposition = response?.headers?.['content-disposition'];
    if (!disposition) return fallback;
    const match = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
    if (match?.[1]) return match[1].replace(/['"]/g, '');
    return fallback;
  };

  const getLedgerExportParams = () => {
    const params = {};
    if (fromDate) params.from_date = fromDate;
    if (toDate) params.to_date = toDate;
    if (tenantFilter) {
      const id = Number(tenantFilter);
      params.tenant_id = id;
      params.agency_id = id;
    }
    if (actionFilter) params.action = actionFilter;
    return params;
  };

  const downloadBlobResponse = (response, fallbackName) => {
    const filename = getDownloadFilename(response, fallbackName);
    const url = URL.createObjectURL(response.data);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const exportCreditLedger = async () => {
    if (exportingLedger) return;
    setExportingLedger(true);
    try {
      const response = await downloadPricingCreditLedger(getLedgerExportParams());
      if (response?.status < 400 && response?.data instanceof Blob) {
        const fallbackName = `credit-ledger-${new Date().toISOString().slice(0, 10)}.xlsx`;
        downloadBlobResponse(response, fallbackName);
      } else {
        notifyError('Failed to export credit ledger');
      }
    } catch (e) {
      notifyError(e?.response?.data?.message || e?.message || 'Failed to export credit ledger');
    } finally {
      setExportingLedger(false);
    }
  };

  const exportConfigChangeLogs = async () => {
    if (exportingLog) return;
    setExportingLog(true);
    try {
      const response = await downloadPricingConfigChangeLogs();
      if (response?.status < 400 && response?.data instanceof Blob) {
        const fallbackName = `config-change-logs-${new Date().toISOString().slice(0, 10)}.xlsx`;
        downloadBlobResponse(response, fallbackName);
      } else {
        notifyError('Failed to export config change log');
      }
    } catch (e) {
      notifyError(e?.response?.data?.message || e?.message || 'Failed to export config change log');
    } finally {
      setExportingLog(false);
    }
  };

  const getSign = (value) => {
    if (value == "add") return '+';
    if (value == "deduct") return '-';
    return '';
  };

  return (
    <>
      <PageHead title="Audit & access" subtitle="Ledger, change log and access" />

      <SectionTitle>Credit ledger</SectionTitle>
      <Panel>
        <div className={styles.filterBar}>
          <span className={styles.filterLabel}>Query</span>
          <Select
            small
            value={tenantFilter || ''}
            onChange={(v) => onFilterChange(setTenantFilter)(v)}
            options={[{ value: '', label: 'All tenants' }, ...(tenants.map((t) => ({ value: String(t.id), label: t.name })))]}
          />
          <span className={styles.inp}>
            <span className={styles.inpPrefix}>From</span>
            <input type="date" value={fromDate} onChange={(e) => onFilterChange(setFromDate)(e.target.value)} style={{ width: 140 }} />
          </span>
          <span className={styles.inp}>
            <span className={styles.inpPrefix}>To</span>
            <input type="date" value={toDate} onChange={(e) => onFilterChange(setToDate)(e.target.value)} style={{ width: 140 }} />
          </span>
          <Select
            small
            value={actionFilter || ''}
            onChange={(v) => onFilterChange(setActionFilter)(v)}
            options={[
              { value: '', label: 'All actions' },
              { value: 'persona generation', label: 'Persona generation' },
              { value: 'queries generation', label: 'Query generation' },
              { value: 'contract creation', label: 'New Contracts' },
              { value: 'translation', label: 'Results translation' },
              { value: 'top-up', label: 'Top-up order' },
              { value: 'grant', label: 'Grant' },
              { value: 'adjustment', label: 'Adjustment' },
            ]}
          />
        </div>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Timestamp (UTC)</th>
              <th>Agency ID</th>
              <th>Agency</th>
              <th>Actor</th>
              <th>Action</th>
              <th className={styles.alignRight}>Credits</th>
              <th>Tier</th>
              {/* <th>Ref</th> */}
            </tr>
          </thead>
          <tbody>
            {loadingLedger ? (
              <tr>
                <td colSpan={8} className={styles.muted}>
                  Loading...
                </td>
              </tr>
            ) : ledgerRows.length ? (
              ledgerRows.map((row, i) => (
                <tr key={i}>
                  <td className={styles.muted}>{display(formatDate(row.timestamp, true))}</td>
                  <td className={styles.muted}>{row.agencyId ? `MPSA-${row.agencyId}` : '—'}</td>
                  <td>{display(row.agency)}</td>
                  <td>{display(row.actor)}</td>
                  {/* INV-09: the forfeit entry of an override, next to its "Contract creation" entry. */}
                  <td>{row.action === 'contract override' ? 'Plan credits forfeited' : display(_.capitalize(row.action))}</td>
                  <td className={styles.alignRight}>
                    {row.tier === 'U' && CREDIT_ALLOTMENT_ACTIONS.has(String(row.action || '').toLowerCase())
                      ? 'Unlimited'
                      : `${getSign(row.type)} ${display(row.credits)}`}
                  </td>
                  <td>{display(row.tier)}</td>
                  {/* <td className={styles.muted}>{display(row.ref)}</td> */}
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={8} className={styles.muted}>
                  No ledger entries
                </td>
              </tr>
            )}
          </tbody>
        </table>
        <ActionsBar>
          {/* <Btn ghost sm>
            Reconstruct balance
          </Btn> */}
          <Btn ghost sm onClick={exportCreditLedger} disabled={exportingLedger}>
            {exportingLedger ? 'Exporting…' : 'Export'}
          </Btn>
          <span className={styles.actionsSpacer} />
          {/* <span className={styles.filterLabel}>Retention</span>
          <div className={`${styles.inp} ${styles.inpW70}`}>
            <input value={retentionYears} onChange={(e) => setRetentionYears(e.target.value)} />
            <span className={styles.inpSuffix}>years (GoBD)</span>
          </div>
          <Btn sm onClick={saveRetention}>
            Save
          </Btn> */}
          <span style={{ width: 12 }} />
          <Btn ghost sm onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1}>
            <ChevronLeft size={14} />
          </Btn>
          <span className={styles.muted} style={{ margin: '0 8px' }}>Page {page} / {totalPages}</span>
          <Btn ghost sm onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages}>
            <ChevronRight size={14} />
          </Btn>
        </ActionsBar>
      </Panel>

      <SectionTitle>Config-change log</SectionTitle>
      <Panel>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>When</th>
              <th>User</th>
              <th>Change</th>
            </tr>
          </thead>
          <tbody>
            {configLoading ? (
              <tr>
                <td colSpan={3} className={styles.muted}>
                  Loading...
                </td>
              </tr>
            ) : changeLog.length ? (
              changeLog.map((row, i) => (
              <tr key={display(row.when) || i}>
                <td className={styles.muted}>{display(formatDate(row.when, true))}</td>
                <td>{display(row.by)}</td>
                <td>{display(row.what)}</td>
              </tr>
            ))
            ) : (
              <tr>
                <td colSpan={3} className={styles.muted}>
                  No change log entries
                </td>
              </tr>
            )}
          </tbody>
        </table>
        <ActionsBar>
          <Btn ghost sm onClick={exportConfigChangeLogs} disabled={exportingLog}>
            {exportingLog ? 'Exporting…' : 'Export log'}
          </Btn>
          <div>

            <Btn ghost sm onClick={() => setConfigPage((p) => Math.max(1, p - 1))} disabled={configPage <= 1}>
              <ChevronLeft size={14} />
            </Btn>
            <span className={styles.muted} style={{ margin: '0 8px' }}>Page {configPage} / {configTotalPages}</span>
            <Btn ghost sm onClick={() => setConfigPage((p) => Math.min(configTotalPages, p + 1))} disabled={configPage >= configTotalPages}>
              <ChevronRight size={14} />
            </Btn>
          </div>
        </ActionsBar>
      </Panel>
    </>
  );
}
